import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
} from 'reactstrap';
import { useCreateEventMutation } from '../../../graphql/mutations/createEvent.generated';
import { useOnUploadFileHook } from '../../../hooks/useOnUploadFileHook';
import { RequestUploadUrlType } from '../../../graphql/generatedTypes';
import moment from 'moment/moment';
import { AllEventsDocument } from '../../../graphql/queries/allEvents.generated';
import { useHistory } from 'react-router';
import { LayoutStoreInstance } from '../../../LayoutStore';
import LabeledCheckBox from '../../../components/LabeledCheckBox';
import { FaArrowLeft, FaCheck } from 'react-icons/fa';
import { ContentSection } from '../../../components/Layout/ContentSection';
import { MainHeader } from '../../../components/Layout/MainHeader';
import i18n from '@/utils/i18n';
import { Routes } from '@/utils/routes';
import { CustomModal } from '../../../components/CustomModal';
import { CreateEventMutation } from '../../../graphql/mutations/createEvent.generated';

type CreatedEventData = NonNullable<CreateEventMutation['createEvent']>;

const CreateEventForm: React.FC = () => {
  const history = useHistory();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isAuthorizedSignerNecessary, setIsAuthorizedSignerNecessary] =
    useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdEventData, setCreatedEventData] =
    useState<CreatedEventData | null>(null);

  const {
    loading: uploadLoading,
    getUrlForS3FileUploadOnFileInputChangeEvent,
  } = useOnUploadFileHook(RequestUploadUrlType.AppTranslation);

  const [createEvent, { loading: createLoading, error }] =
    useCreateEventMutation();

  const handleCreateEvent = async () => {
    // image_url is optional on CreateEventInput — requiring it here blocks
    // event creation entirely wherever the S3 upload is unavailable.
    if (!name || !description || !startDate || !endDate) {
      alert('Name, description, start date and end date are required');
      return;
    }

    const result = await createEvent({
      variables: {
        input: {
          name,
          description,
          image_url: imageUrl || undefined,
          start_date: startDate,
          end_date: endDate,
          is_authorized_signer_necessary: isAuthorizedSignerNecessary,
        },
      },
      refetchQueries: [
        {
          query: AllEventsDocument,
        },
      ],
    });

    if (result.data?.createEvent) {
      LayoutStoreInstance.setSuccess('Event created');
      setCreatedEventData(result.data.createEvent);
      setShowSuccessModal(true);
    }
    if (result.errors) {
      LayoutStoreInstance.setError('Error creating event');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = await getUrlForS3FileUploadOnFileInputChangeEvent(e);
    if (url) {
      setImageUrl(url);
      return;
    }
    setImageUrl('');
    LayoutStoreInstance.setError(
      'Image upload failed — the event can still be created without one',
    );
  };

  const handleModalAccept = () => {
    if (createdEventData?.id) {
      history.push(Routes.eventDetail(createdEventData.id));
    }
  };

  const renderSuccessModalBody = () => {
    if (!createdEventData) return <div>No event data available</div>;

    return (
      <div>
        {/* Event Title */}
        <div className="mb-3">
          <div style={{ marginBottom: '15px' }}>
            📅 <strong>{i18n.t('Events:event')}:</strong>{' '}
            <a
              href={Routes.eventDetail(createdEventData.id)}
              className="text-primary"
            >
              "{createdEventData.name}"
            </a>
          </div>

          {/* Event Accounts Section */}
          {createdEventData.event_participants &&
            createdEventData.event_participants.length > 0 && (
              <div style={{ marginLeft: '20px' }}>
                <div style={{ marginBottom: '10px' }}>
                  👥{' '}
                  <strong>
                    {i18n.t('Events:eventAccounts', {
                      count: createdEventData.event_participants.length,
                    })}
                    :
                  </strong>
                </div>
                {createdEventData.event_participants.map((account) => (
                  <div
                    key={account.id}
                    style={{ marginBottom: '15px', marginLeft: '20px' }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      ● 🏢{' '}
                      <a
                        href={Routes.eventAccountDetail(account.id)}
                        className="text-primary"
                      >
                        "{account.name}"
                      </a>
                    </div>

                    {/* Item Locations */}
                    {account.item_locations &&
                      account.item_locations.length > 0 && (
                        <div style={{ marginLeft: '20px' }}>
                          <div style={{ marginBottom: '8px' }}>
                            📍{' '}
                            <strong>
                              {i18n.t('Events:itemLocations', {
                                count: account.item_locations.length,
                              })}
                              :
                            </strong>
                          </div>
                          {account.item_locations.map((location) => (
                            <div
                              key={location.id}
                              style={{
                                marginBottom: '5px',
                                marginLeft: '20px',
                              }}
                            >
                              ● 📦{' '}
                              <a
                                href={Routes.itemLocation(location.id)}
                                className="text-primary"
                              >
                                "{location.name}"
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    );
  };

  return (
    <>
      <MainHeader title={i18n.t('Events:events')} />
      <ContentSection title={i18n.t('Common:createEvent')} collapsable={false}>
        <Button
          className="float-end mt-2 mb-2"
          onClick={() => history.goBack()}
        >
          <FaArrowLeft /> Back
        </Button>

        <Form style={{ clear: 'both' }}>
          <FormGroup>
            <Label for="eventName">{i18n.t('Common:name')}</Label>
            <Input
              type="text"
              name="name"
              id="eventName"
              placeholder={i18n.t('Common:eventName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label for="eventDescription">{i18n.t('Common:description')}</Label>
            <Input
              type="textarea"
              name="description"
              id="eventDescription"
              placeholder={i18n.t('Common:eventDescription')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label for="eventStartDate">Start Date</Label>
            <Input
              type="date"
              value={startDate ?? moment().format('YYYY-MM-DD')}
              name="event-start-date"
              onChange={(e) => {
                console.log(e.target.value);
                const newDate = new Date(e.target.value);
                setStartDate(moment(newDate).format('YYYY-MM-DD'));
              }}
            />
          </FormGroup>
          <FormGroup>
            <Label for="eventEndDate">End Date</Label>
            <Input
              type="date"
              value={endDate ?? moment().format('YYYY-MM-DD')}
              name="event-end-date"
              onChange={(e) => {
                console.log(e.target.value);
                const newDate = new Date(e.target.value);
                setEndDate(moment(newDate).format('YYYY-MM-DD'));
              }}
            />
          </FormGroup>
          <FormGroup>
            <Label for="eventImage">Image</Label>
            <Input
              type="file"
              name="image"
              id="eventImage"
              onChange={handleImageUpload}
            />
            {uploadLoading && (
              <FormText color="muted">Uploading image...</FormText>
            )}
            {imageUrl && (
              <img
                src={imageUrl}
                alt="Event"
                style={{ maxWidth: '200px', marginTop: '10px' }}
              />
            )}
          </FormGroup>
          <FormGroup>
            <LabeledCheckBox
              checked={isAuthorizedSignerNecessary}
              onClick={() =>
                setIsAuthorizedSignerNecessary(!isAuthorizedSignerNecessary)
              }
            >
              Is Authorized Signer Necessary
            </LabeledCheckBox>
          </FormGroup>
          {error && <Alert color="danger">Error: {error.message}</Alert>}
          <Button
            color="primary"
            onClick={handleCreateEvent}
            disabled={createLoading || uploadLoading}
          >
            Create Event
          </Button>
        </Form>
      </ContentSection>

      {/* Success Modal */}
      <CustomModal
        isOpen={showSuccessModal}
        setIsOpen={setShowSuccessModal}
        header={i18n.t('Events:eventCreatedSuccessfully')}
        body={renderSuccessModalBody()}
        acceptButtonText="View Event"
        acceptButtonIcon={<FaCheck />}
        acceptButtonAction={handleModalAccept}
        acceptButtonDisabled={!createdEventData?.id}
        showCancelButton={false}
      />
    </>
  );
};

export default CreateEventForm;
